import type { GenerationModelCatalogEntry, ProjectSummary } from "@/types/workspace"

export function findShotVideoModel(
  modelKey: string | null | undefined,
  models: readonly GenerationModelCatalogEntry[] | null | undefined,
): GenerationModelCatalogEntry | null {
  const id = modelKey?.toString().trim()
  if (!id || !models?.length) return null
  return models.find((m) => m.id === id) ?? null
}

export function effectiveShotVideoModelKey(
  shotOverride: string | null | undefined,
  project: ProjectSummary | null | undefined,
): string {
  return shotOverride?.toString().trim() || project?.effective_video_model?.toString().trim() || ""
}

export function resolutionParamOptions(model: GenerationModelCatalogEntry | null | undefined): string[] {
  const param = model?.extra_params?.find((p) => p.key === "resolution")
  return Array.isArray(param?.options) ? param.options.map(String) : []
}

export function resolutionParamDefault(model: GenerationModelCatalogEntry | null | undefined): string {
  const param = model?.extra_params?.find((p) => p.key === "resolution")
  if (param?.default == null || param.default === "") return ""
  return String(param.default).trim()
}

export function projectResolutionForProvider(
  project: ProjectSummary | null | undefined,
  provider: string | null | undefined,
): string {
  if (!project) return ""
  switch (provider) {
    case "openrouter_video":
      return project.openrouter_video_resolution?.toString().trim() || "480p"
    case "happyhorse_video":
      return project.happyhorse_video_resolution?.toString().trim() || "720P"
    case "byteplus_seedance_video":
      return project.byteplus_seedance_video_resolution?.toString().trim() || "720p"
    case "google_veo":
      return project.google_veo_resolution?.toString().trim() || ""
    default:
      // wan / kling / unknown: Ruby project_resolution_for returns nil (no project column).
      return ""
  }
}

/** Studio inherit for chip/display: project column, else catalog resolution default. */
export function inheritedShotVideoResolution(
  project: ProjectSummary | null | undefined,
  model: GenerationModelCatalogEntry | null | undefined,
): string {
  const fromProject = projectResolutionForProvider(project, model?.provider)
  if (fromProject) return fromProject

  const opts = resolutionParamOptions(model)
  const catalogDefault = resolutionParamDefault(model)
  if (catalogDefault && (opts.length === 0 || opts.includes(catalogDefault))) return catalogDefault
  return ""
}

export function effectiveShotVideoResolution(
  shotOverride: string | null | undefined,
  project: ProjectSummary | null | undefined,
  model: GenerationModelCatalogEntry | null | undefined,
): string {
  const override = shotOverride?.toString().trim()
  const opts = resolutionParamOptions(model)
  if (override && (opts.length === 0 || opts.includes(override))) return override
  return inheritedShotVideoResolution(project, model)
}

/** Keep shot resolution override only when still valid for the next effective model (mirrors server). */
export function videoResolutionAfterModelChange(
  currentResolution: string | null | undefined,
  nextModelKey: string | null | undefined,
  models: readonly GenerationModelCatalogEntry[] | null | undefined,
): string | null {
  const override = currentResolution?.toString().trim() || null
  if (!override) return null

  const model = findShotVideoModel(nextModelKey, models)
  const opts = resolutionParamOptions(model)
  if (opts.length === 0 || opts.includes(override)) return override
  return null
}

export function resolutionRank(resolution: string | null | undefined): number | null {
  const s = resolution?.toString().trim().toLowerCase() ?? ""
  if (!s) return null
  if (/480/.test(s)) return 480
  if (/720/.test(s)) return 720
  if (/1080/.test(s)) return 1080
  if (/4k|2160/.test(s)) return 2160
  return null
}

/** Lowest ranked option string from the model catalog (e.g. 480p, or 720p if no 480). */
export function lowestResolutionOption(options: readonly string[]): string | null {
  let best: string | null = null
  let bestRank = Infinity
  for (const option of options) {
    const rank = resolutionRank(option)
    if (rank == null || rank >= bestRank) continue
    best = option
    bestRank = rank
  }
  return best
}

export function shouldConfirmFirstGenResolution(args: {
  hasVideo: boolean
  effectiveResolution: string
  model: GenerationModelCatalogEntry | null | undefined
}): boolean {
  if (args.hasVideo) return false
  const opts = resolutionParamOptions(args.model)
  const lowest = lowestResolutionOption(opts)
  if (!lowest) return false
  const effectiveRank = resolutionRank(args.effectiveResolution)
  const lowestRank = resolutionRank(lowest)
  return effectiveRank != null && lowestRank != null && effectiveRank > lowestRank
}
